// Reel Transitions — dedicated highlights tab (dev25).
//
// Consolidates all reel-transition workflow into one page:
//   1. Bumper Library — CRUD table for the reel_transitions D1 registry.
//      Extracted from SettingsEditor.jsx (formerly TransitionsPanel) so
//      editors don't dig through the crowded Settings modal to change bumper
//      wiring. Same table columns, same API calls, no schema changes.
//   2. Create New Bumper — new 3-step self-service flow:
//      a) ffmpeg command generator — editor supplies source filename,
//         we render a copy-paste ffmpeg recipe targeting the canonical
//         spec (1080p @ 59.94 fps H.264 High, AAC-LC 48 kHz stereo,
//         matches black_1s byte-for-byte).
//      b) Upload to Mux — reuses MuxUpload from mux-stream-manager
//         verbatim (direct-upload flow, progress bar, cancel).
//      c) Registry entry form — auto-populates from Mux asset details,
//         includes a spec-check warning if the uploaded asset doesn't
//         meet transition requirements. POSTs to the same
//         /admin/reel-transitions endpoint the inline "Add transition"
//         button used before.
//
// Reuses:
//   - useHighlightsApi.listReelTransitionsAll/create/patch/delete
//   - MuxUpload component from mux-stream-manager
//   - GET /mux/assets/:id (via AuthContext api) for asset detail lookup
//   - MuxStreamManager.css for MuxUpload styling
//   - Existing muxAssetsCache for the Pick-from-Mux search
//
// Zero backend changes required — same worker routes, same D1 schema.

import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  Loader2, Check, Pencil, Trash2, Plus, Film, Search, RefreshCw, Copy,
  Upload, AlertTriangle, Zap,
} from 'lucide-react'
import { useHighlightsApi } from './useHighlightsApi'
import { useAuth } from '../../../context/AuthContext'
import { readCache as readMuxAssetsCache, writeCache as writeMuxAssetsCache } from '../../../lib/muxAssetsCache'
import MuxUpload from '../../video-library/mux-stream-manager/MuxUpload'
import '../../video-library/mux-stream-manager/MuxStreamManager.css'

// Shared spec check — returns { ok: bool, issues: string[] } given a Mux
// asset JSON (from /mux/assets/:id or the list endpoint). Used by:
//   - BumperLibrary spec validator (badge each row on library load)
//   - BumperCreator's Step 3 spec-check banner
function checkAssetSpec(asset) {
  const issues = []
  const tracks = asset?.tracks || []
  const video = tracks.find(t => t.type === 'video')
  const audio = tracks.find(t => t.type === 'audio')

  if (!video) {
    issues.push('No video track present.')
  } else {
    if (video.max_width && video.max_width < 1920) {
      issues.push(`Video width is ${video.max_width}, target 1920. Roku prefers 1080p.`)
    }
    if (video.max_height && video.max_height < 1080) {
      issues.push(`Video height is ${video.max_height}, target 1080.`)
    }
    if (video.max_frame_rate && Math.abs(video.max_frame_rate - 59.94) > 1 && Math.abs(video.max_frame_rate - 60) > 1) {
      issues.push(`Video frame rate is ${video.max_frame_rate} fps, target 59.94. Frame rate mismatches can cause playback stutter across DISCONTINUITY boundaries.`)
    }
  }

  if (!audio) {
    issues.push('No audio track present. This will cause decoder confusion at reel transition boundaries. Re-encode with the ffmpeg recipe below to add a silent AAC track.')
  } else if (audio.max_channels && audio.max_channels !== 2) {
    issues.push(`Audio has ${audio.max_channels} channel(s), target 2 (stereo).`)
  }

  return { ok: issues.length === 0, issues }
}

// Backend accepts only lowercase alphanumerics + underscore in registry
// keys (KEY_PATTERN in routes/reel-transitions.js). Slugify user input as
// they type so they can't accidentally submit an invalid key.
const KEY_PATTERN = /^[a-z0-9_]+$/
function slugifyKey(input) {
  return (input || '')
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')  // any non-conforming char → underscore
    .replace(/_+/g, '_')            // collapse runs
    .replace(/^_+|_+$/g, '')        // trim leading/trailing underscores
}

// Canonical transition spec — matches the working black_1s bumper
// byte-for-byte. Used by:
//   - the ffmpeg command generator (target of the encode)
//   - the spec-check in the registry form (verify uploaded assets)
const TARGET_SPEC = {
  width: 1920,
  height: 1080,
  fps: '60000/1001',   // 59.94 — matches Mux 1080p variant
  fps_display: '59.94',
  video_codec: 'H.264 High',
  pix_fmt: 'yuv420p',
  color: 'BT.709',
  audio_codec: 'AAC-LC',
  audio_sample_rate: 48000,
  audio_channels: 2,
  audio_bitrate_kbps: 128,
  container: 'MP4 (faststart)',
}

// ============================================================================
// PAGE CONTAINER
// ============================================================================

export default function ReelTransitions() {
  const api = useHighlightsApi()
  const [rows, setRows] = useState(null)
  const [err, setErr] = useState(null)

  const refresh = useCallback(() => {
    setErr(null)
    api.listReelTransitionsAll()
      .then(r => setRows(r?.transitions || []))
      .catch(e => setErr(e.message || String(e)))
  }, [api])

  useEffect(() => { refresh() }, [refresh])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1100 }}>
      <BumperLibrary
        rows={rows}
        err={err}
        setErr={setErr}
        refresh={refresh}
        api={api}
      />
      <BumperCreator
        onCreated={refresh}
        api={api}
      />
    </div>
  )
}

// ============================================================================
// SECTION 1 — BUMPER LIBRARY (extracted from SettingsEditor)
// ============================================================================

function BumperLibrary({ rows, err, setErr, refresh, api }) {
  const { api: rawApi } = useAuth()
  const [editing, setEditing] = useState(null)  // null | 'new' | key string
  const [busyKey, setBusyKey] = useState(null)

  // Spec validator — for each populated row, look up its Mux asset and
  // compare against TARGET_SPEC. Runs once when rows populate; also on
  // manual re-validate click. Batches the fetch via /mux/assets (returns
  // all assets — one request regardless of row count) and matches by
  // playback_id. Cheap: reuses the same shared muxAssetsCache used by
  // MuxAssetPicker so cross-tab it's usually already warm.
  const [specByKey, setSpecByKey] = useState({})  // { key: { ok, issues[] } }
  const [validating, setValidating] = useState(false)

  const validate = useCallback(async () => {
    if (!rows || rows.length === 0) return
    setValidating(true)
    try {
      // Load all Mux assets (cached), build playback_id → asset lookup
      let assets = readMuxAssetsCache()
      if (!assets) {
        const d = await rawApi('/mux/assets')
        assets = d?.data || d || []
        writeMuxAssetsCache(assets)
      }
      const byPb = new Map()
      for (const a of assets) {
        const pb = a.playback_ids?.[0]?.id
        if (pb) byPb.set(pb, a)
      }
      const next = {}
      for (const row of rows) {
        if (!row.mux_playback_id) continue // placeholder, nothing to check
        const asset = byPb.get(row.mux_playback_id)
        if (!asset) {
          next[row.key] = { ok: false, issues: ['Mux asset not found (may be deleted)'] }
          continue
        }
        next[row.key] = checkAssetSpec(asset)
      }
      setSpecByKey(next)
    } catch (e) {
      // Non-fatal — library still functional, just no spec badges
      console.warn('spec validation failed:', e.message)
    } finally {
      setValidating(false)
    }
  }, [rows, rawApi])

  // Auto-run when rows populate + on refresh
  useEffect(() => { validate() }, [validate])

  const populated = (rows || []).filter(r => r.mux_playback_id && r.is_enabled).length
  const total = (rows || []).length

  async function toggleEnabled(row) {
    setBusyKey(row.key)
    try {
      await api.patchReelTransition(row.key, { is_enabled: row.is_enabled ? 0 : 1 })
      refresh()
    } catch (e) {
      setErr(e.message || String(e))
    } finally {
      setBusyKey(null)
    }
  }

  // Promote one row to the reel-editor default. Server enforces the
  // "exactly one row = 1" invariant by demoting every other row in the
  // same batch, so we don't need to touch the other rows client-side.
  async function setDefault(row) {
    if (row.is_default) return
    setBusyKey(row.key)
    try {
      await api.patchReelTransition(row.key, { is_default: true })
      refresh()
    } catch (e) {
      setErr(e.message || String(e))
    } finally {
      setBusyKey(null)
    }
  }

  async function remove(row) {
    if (!window.confirm(
      `Delete transition "${row.key}"?\n\n` +
      `Any reel segments still pointing at this key will fall back to a hard cut ` +
      `(same behavior as an unknown key). This cannot be undone.`
    )) return
    setBusyKey(row.key)
    try {
      const r = await api.deleteReelTransition(row.key)
      if (r?.orphaned_segment_references > 0) {
        window.alert(
          `Deleted. ${r.orphaned_segment_references} reel segment(s) still ` +
          `referenced this key — they now hard-cut at that gap.`
        )
      }
      refresh()
    } catch (e) {
      setErr(e.message || String(e))
    } finally {
      setBusyKey(null)
    }
  }

  return (
    <div style={{ border: '1px solid #333', borderRadius: 6, background: '#161616' }}>
      {/* Section header — no collapsibility (dedicated page, always visible) */}
      <div style={{
        padding: '10px 14px', borderBottom: '1px solid #2a2a2a',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <Film size={16} color="#3b82f6" />
        <div style={{ fontWeight: 600, fontSize: 14 }}>Bumper Library</div>
        <span style={{ fontSize: 11, color: '#888' }}>
          {rows == null ? 'loading…' : `${populated} available · ${total} total`}
        </span>
        <button
          onClick={() => validate()}
          disabled={validating || !rows}
          title="Re-validate spec of every populated bumper against target (batch fetches Mux asset metadata)"
          style={{
            marginLeft: 'auto', padding: '4px 10px', background: 'transparent',
            border: '1px solid #333', borderRadius: 3, color: '#888',
            cursor: 'pointer', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
          {validating ? <Loader2 size={12} className="spin" /> : <Check size={12} />}
          Validate specs
        </button>
        <button
          onClick={refresh}
          disabled={rows == null}
          title="Refresh"
          style={{
            padding: '4px 10px', background: 'transparent',
            border: '1px solid #333', borderRadius: 3, color: '#888',
            cursor: 'pointer', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div style={{ padding: 14 }}>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 12, lineHeight: 1.5 }}>
          Bumpers inserted between clips when a reel's <code>transitions_enabled</code> flag
          is on. Rows without a Mux playback ID or with the enabled flag off are treated
          as unknown by the stitcher — that gap falls back to a hard cut.
        </div>

        {err && <div style={{ color: 'crimson', fontSize: 12, marginBottom: 10 }}>{err}</div>}

        {rows == null && (
          <div style={{ padding: 20, textAlign: 'center', color: '#888', fontSize: 12 }}>
            <Loader2 size={14} className="spin" style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Loading registry…
          </div>
        )}

        {rows && rows.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', color: '#888', fontSize: 12 }}>
            No bumpers in the registry yet. Add one below or via the Bumper Creator.
          </div>
        )}

        {rows && rows.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'auto' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #333', color: '#888' }}>
                  <th style={{ textAlign: 'left', padding: '6px 4px', fontWeight: 500 }}>Key / Label</th>
                  <th style={{ textAlign: 'left', padding: '6px 4px', fontWeight: 500 }}>Playback ID</th>
                  <th style={{ textAlign: 'right', padding: '6px 4px', fontWeight: 500, width: '50px' }}>Dur</th>
                  <th style={{ textAlign: 'center', padding: '6px 4px', fontWeight: 500, width: '60px' }}
                      title="Applied to every transition point when an editor enables transitions on a reel">
                    Default
                  </th>
                  <th style={{ textAlign: 'right', padding: '6px 4px', fontWeight: 500, width: '180px', whiteSpace: 'nowrap' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => {
                  const isEditing = editing === row.key
                  const placeholder = !row.mux_playback_id
                  const shortPb = row.mux_playback_id
                    ? `${row.mux_playback_id.slice(0, 10)}…`
                    : null
                  return (
                    <Fragment key={row.key}>
                      <tr style={{
                        borderBottom: '1px solid #222',
                        opacity: placeholder ? 0.7 : 1,
                      }}>
                        <td style={{ padding: '6px 4px', color: '#ddd' }}>
                          <div style={{ fontFamily: 'monospace', fontSize: 12 }}>{row.key}</div>
                          <div style={{ fontSize: 11, color: '#888' }}>{row.label}</div>
                        </td>
                        <td style={{ padding: '6px 4px', fontFamily: 'monospace', color: placeholder ? '#666' : '#aaa', fontSize: 11, whiteSpace: 'nowrap' }}
                            title={row.mux_playback_id || 'no playback ID yet'}>
                          {shortPb || <em style={{ color: '#f59e0b' }}>not uploaded</em>}
                          {/* Spec badge — only shown for populated rows once validate has run */}
                          {row.mux_playback_id && specByKey[row.key] && (
                            <SpecBadge result={specByKey[row.key]} />
                          )}
                        </td>
                        <td style={{ padding: '6px 4px', textAlign: 'right', color: '#aaa', whiteSpace: 'nowrap' }}>
                          {row.duration_seconds}s
                        </td>
                        <td style={{ padding: '6px 4px', textAlign: 'center' }}
                            title={placeholder
                              ? 'Upload a Mux playback ID before setting as default'
                              : (row.is_default
                                  ? 'This is the current default bumper'
                                  : 'Set as default — applied on every transition when an editor enables transitions')}>
                          <input
                            type="radio"
                            name="default-bumper"
                            checked={row.is_default === 1}
                            disabled={busyKey === row.key || placeholder || !row.is_enabled}
                            onChange={() => setDefault(row)}
                            style={{ cursor: (placeholder || !row.is_enabled) ? 'not-allowed' : 'pointer' }}
                          />
                        </td>
                        <td style={{ padding: '6px 4px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button
                            onClick={() => toggleEnabled(row)}
                            disabled={busyKey === row.key}
                            title={row.is_enabled ? 'Disable (stitcher will ignore)' : 'Enable'}
                            style={{
                              padding: '2px 8px', borderRadius: 10,
                              border: 'none', cursor: 'pointer', fontSize: 10,
                              background: row.is_enabled ? '#166534' : '#333',
                              color: row.is_enabled ? '#bbf7d0' : '#888',
                              fontWeight: 600, marginRight: 6,
                            }}>
                            {busyKey === row.key ? '…' : (row.is_enabled ? 'ON' : 'OFF')}
                          </button>
                          <button
                            onClick={() => setEditing(isEditing ? null : row.key)}
                            title="Edit"
                            style={{
                              background: 'transparent', border: '1px solid #333', borderRadius: 3,
                              color: '#ddd', cursor: 'pointer', padding: '4px 6px', marginRight: 4,
                            }}>
                            <Pencil size={12} />
                          </button>
                          <button
                            onClick={() => remove(row)}
                            disabled={busyKey === row.key}
                            title="Delete"
                            style={{
                              background: 'transparent', border: '1px solid #4a1414', borderRadius: 3,
                              color: '#dc2626', cursor: 'pointer', padding: '4px 6px',
                            }}>
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>
                      {isEditing && (
                        <tr>
                          <td colSpan={5} style={{ padding: '10px 4px 14px', background: '#0e0e0e' }}>
                            <TransitionEditForm
                              row={row}
                              onCancel={() => setEditing(null)}
                              onSaved={() => { setEditing(null); refresh() }}
                              onError={e => setErr(e.message || String(e))}
                              api={api}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {editing === 'new' ? (
          <div style={{ padding: 10, background: '#0e0e0e', border: '1px solid #333', borderRadius: 4 }}>
            <TransitionEditForm
              row={null}
              onCancel={() => setEditing(null)}
              onSaved={() => { setEditing(null); refresh() }}
              onError={e => setErr(e.message || String(e))}
              api={api}
            />
          </div>
        ) : (
          <button
            onClick={() => setEditing('new')}
            style={{
              padding: '6px 12px', background: '#3b82f6', border: 'none',
              borderRadius: 4, color: 'white', cursor: 'pointer', fontSize: 12,
              display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 500,
            }}>
            <Plus size={12} /> Add transition (advanced — paste playback ID directly)
          </button>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// SHARED — TransitionEditForm (extracted from SettingsEditor verbatim)
// ============================================================================

function TransitionEditForm({ row, onCancel, onSaved, onError, api }) {
  const isNew = row === null
  const [key, setKey] = useState(row?.key || '')
  const [label, setLabel] = useState(row?.label || '')
  const [playbackId, setPlaybackId] = useState(row?.mux_playback_id || '')
  const [duration, setDuration] = useState(row?.duration_seconds ?? 1)
  const [kind, setKind] = useState(row?.kind || 'static')
  const [enabled, setEnabled] = useState(row ? row.is_enabled === 1 : true)
  const [sortOrder, setSortOrder] = useState(row?.sort_order ?? 0)
  const [busy, setBusy] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)

  function applyMuxAsset(asset) {
    const pbId = asset.playback_ids?.[0]?.id
    if (!pbId) return
    setPlaybackId(pbId)
    if (typeof asset.duration === 'number' && asset.duration > 0) {
      setDuration(Number(asset.duration.toFixed(3)))
    }
    if (!label.trim() && asset.meta?.title) {
      setLabel(asset.meta.title)
    }
    setPickerOpen(false)
  }

  async function save() {
    setBusy(true)
    try {
      const durationNum = typeof duration === 'string' ? parseFloat(duration) : duration
      const sortNum = typeof sortOrder === 'string' ? parseInt(sortOrder, 10) : sortOrder
      if (isNew) {
        await api.createReelTransition({
          key: key.trim(),
          label: label.trim(),
          mux_playback_id: playbackId.trim() || null,
          duration_seconds: durationNum,
          kind,
          is_enabled: enabled ? 1 : 0,
          sort_order: Number.isFinite(sortNum) ? sortNum : 0,
        })
      } else {
        await api.patchReelTransition(row.key, {
          label: label.trim(),
          mux_playback_id: playbackId.trim() || null,
          duration_seconds: durationNum,
          kind,
          is_enabled: enabled ? 1 : 0,
          sort_order: Number.isFinite(sortNum) ? sortNum : 0,
        })
      }
      onSaved()
    } catch (e) {
      onError(e)
    } finally {
      setBusy(false)
    }
  }

  const inputStyle = {
    padding: '4px 8px', background: '#1a1a1a', border: '1px solid #333',
    borderRadius: 3, color: '#ddd', fontSize: 12,
  }
  const labelStyle = { fontSize: 11, color: '#888', marginBottom: 3, display: 'block' }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, maxWidth: 800 }}>
      <div>
        <label style={labelStyle}>Key {isNew && <span style={{ color: '#666' }}>(a-z, 0-9, _ — permanent, auto-slugified)</span>}</label>
        <input value={key} onChange={e => setKey(isNew ? slugifyKey(e.target.value) : e.target.value)}
          disabled={!isNew || busy}
          placeholder="e.g. brand_bumper_3s" style={{ ...inputStyle, width: '100%', fontFamily: 'monospace' }} />
        {isNew && key && !KEY_PATTERN.test(key) && (
          <div style={{ fontSize: 10, color: '#f59e0b', marginTop: 2 }}>
            Only lowercase letters, digits, and underscores allowed
          </div>
        )}
      </div>
      <div>
        <label style={labelStyle}>Label (shown in editor picker)</label>
        <input value={label} onChange={e => setLabel(e.target.value)} disabled={busy}
          placeholder="e.g. TenbandTV bumper — 3s" style={{ ...inputStyle, width: '100%' }} />
      </div>
      <div style={{ gridColumn: '1 / span 2' }}>
        <label style={labelStyle}>
          Mux playback ID <span style={{ color: '#666' }}>(pick from Mux or paste; leave blank to create a placeholder — stitcher will skip until populated)</span>
        </label>
        <div style={{ display: 'flex', gap: 6 }}>
          <input value={playbackId} onChange={e => setPlaybackId(e.target.value)} disabled={busy}
            placeholder="e.g. Wq00XyxzABC123..."
            style={{ ...inputStyle, flex: 1, fontFamily: 'monospace' }} />
          <button type="button" onClick={() => setPickerOpen(!pickerOpen)} disabled={busy}
            style={{
              padding: '4px 12px', background: pickerOpen ? '#1e40af' : '#333',
              border: 'none', borderRadius: 3, color: '#ddd', cursor: 'pointer',
              fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4,
              whiteSpace: 'nowrap',
            }}>
            <Search size={12} /> Pick from Mux
          </button>
        </div>
        {pickerOpen && (
          <MuxAssetPicker
            currentPlaybackId={playbackId}
            onSelect={applyMuxAsset}
            onClose={() => setPickerOpen(false)}
          />
        )}
      </div>
      <div>
        <label style={labelStyle}>Duration (seconds)</label>
        <input type="number" step="0.1" min="0.1" value={duration}
          onChange={e => setDuration(e.target.value)} disabled={busy}
          style={{ ...inputStyle, width: '100%' }} />
      </div>
      <div>
        <label style={labelStyle}>Kind</label>
        <select value={kind} onChange={e => setKind(e.target.value)} disabled={busy}
          style={{ ...inputStyle, width: '100%' }}>
          <option value="static">static</option>
          <option value="video">video</option>
        </select>
      </div>
      <div>
        <label style={labelStyle}>Sort order (lower = first)</label>
        <input type="number" step="1" value={sortOrder}
          onChange={e => setSortOrder(e.target.value)} disabled={busy}
          style={{ ...inputStyle, width: '100%' }} />
      </div>
      <div>
        <label style={{ ...labelStyle, marginBottom: 8 }}>&nbsp;</label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#ddd', cursor: 'pointer' }}>
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} disabled={busy} />
          Enabled (visible in ReelEditor picker)
        </label>
      </div>
      <div style={{ gridColumn: '1 / span 2', display: 'flex', gap: 8, marginTop: 4 }}>
        <button onClick={save}
          disabled={busy || !key.trim() || !label.trim() || (isNew && !KEY_PATTERN.test(key.trim()))}
          style={{
            padding: '6px 14px', background: '#3b82f6', border: 'none', borderRadius: 4,
            color: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 500,
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
          {busy ? <Loader2 size={12} className="spin" /> : <Check size={12} />}
          {isNew ? 'Create' : 'Save'}
        </button>
        <button onClick={onCancel} disabled={busy}
          style={{
            padding: '6px 14px', background: '#333', border: 'none', borderRadius: 4,
            color: '#ddd', cursor: 'pointer', fontSize: 12,
          }}>
          Cancel
        </button>
      </div>
    </div>
  )
}

// ============================================================================
// SHARED — MuxAssetPicker (extracted from SettingsEditor verbatim)
// ============================================================================

function MuxAssetPicker({ currentPlaybackId, onSelect, onClose }) {
  const { api } = useAuth()
  const [assets, setAssets] = useState(null)
  const [err, setErr] = useState(null)
  const [q, setQ] = useState('')
  const searchRef = useRef(null)

  useEffect(() => {
    const cached = readMuxAssetsCache()
    if (cached) { setAssets(cached); return }
    api('/mux/assets')
      .then(d => {
        const fresh = d?.data || d || []
        setAssets(fresh)
        writeMuxAssetsCache(fresh)
      })
      .catch(e => setErr(e.message || String(e)))
  }, [api])

  useEffect(() => { searchRef.current?.focus() }, [])

  const filtered = useMemo(() => {
    if (!assets) return []
    const needle = q.trim().toLowerCase()
    const list = needle
      ? assets.filter(a => {
          const title = (a.meta?.title || '').toLowerCase()
          const passthrough = (a.passthrough || '').toLowerCase()
          const pbid = (a.playback_ids?.[0]?.id || '').toLowerCase()
          return title.includes(needle) || passthrough.includes(needle) || pbid.includes(needle)
        })
      : assets
    return list.filter(a => a.playback_ids?.[0]?.id).slice(0, 100)
  }, [assets, q])

  return (
    <div style={{
      marginTop: 8, background: '#0a0a0a', border: '1px solid #333',
      borderRadius: 4, padding: 10,
    }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={11} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#666', pointerEvents: 'none' }} />
          <input
            ref={searchRef}
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search by title, passthrough, or playback ID…"
            style={{
              width: '100%', padding: '5px 8px 5px 26px', background: '#1a1a1a',
              border: '1px solid #333', borderRadius: 3, color: '#ddd', fontSize: 12,
            }}
          />
        </div>
        <button type="button" onClick={onClose}
          style={{
            padding: '4px 10px', background: '#333', border: 'none', borderRadius: 3,
            color: '#ddd', cursor: 'pointer', fontSize: 11,
          }}>
          Close
        </button>
      </div>

      {err && <div style={{ color: 'crimson', fontSize: 11, padding: 6 }}>{err}</div>}
      {!assets && !err && (
        <div style={{ color: '#888', fontSize: 11, padding: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Loader2 size={11} className="spin" /> Loading Mux assets…
        </div>
      )}
      {assets && filtered.length === 0 && (
        <div style={{ color: '#888', fontSize: 11, padding: 10 }}>
          {q ? `No matches for "${q}"` : 'No assets available'}
        </div>
      )}
      {assets && filtered.length > 0 && (
        <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #222', borderRadius: 3 }}>
          {filtered.map(a => {
            const pbId = a.playback_ids[0].id
            const selected = pbId === currentPlaybackId
            const durStr = typeof a.duration === 'number' ? `${a.duration.toFixed(2)}s` : '—'
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => onSelect(a)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto auto',
                  gap: 10, alignItems: 'center',
                  width: '100%', padding: '6px 10px',
                  background: selected ? 'rgba(59,130,246,0.12)' : 'transparent',
                  border: 'none', borderBottom: '1px solid #1a1a1a',
                  color: '#ddd', cursor: 'pointer', textAlign: 'left',
                  fontSize: 12,
                }}
                onMouseEnter={e => { if (!selected) e.currentTarget.style.background = '#1a1a1a' }}
                onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'transparent' }}
              >
                <div style={{ minWidth: 0, overflow: 'hidden' }}>
                  <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {a.meta?.title || a.passthrough || <em style={{ color: '#666' }}>(untitled)</em>}
                    {selected && <Check size={11} style={{ marginLeft: 6, color: '#3b82f6', verticalAlign: 'middle' }} />}
                  </div>
                  <div style={{ fontSize: 10, color: '#666', fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {pbId}
                  </div>
                </div>
                <span style={{ fontSize: 10, color: '#888', whiteSpace: 'nowrap' }}>{durStr}</span>
                <span style={{
                  fontSize: 9, padding: '1px 6px', borderRadius: 8,
                  background: a.status === 'ready' ? '#166534' : '#78350f',
                  color: a.status === 'ready' ? '#bbf7d0' : '#fed7aa',
                }}>{a.status || '?'}</span>
              </button>
            )
          })}
          {assets && filtered.length === 100 && (
            <div style={{ padding: 6, fontSize: 10, color: '#666', textAlign: 'center' }}>
              Showing first 100 matches — refine the search to narrow down.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// SECTION 2 — BUMPER CREATOR (NEW)
// ============================================================================

function BumperCreator({ onCreated, api }) {
  const { api: rawApi } = useAuth()  // for /mux/assets/:id direct calls

  // Step 1 state — command generator inputs. Simplified: no checkboxes.
  // Bumpers always get silent AAC (we always want silent transitions in
  // reels — the bumper carries no sound). Duration is auto-detected by
  // the generated bash snippet's ffprobe call, so editor doesn't need to
  // supply it.
  const [sourceName, setSourceName] = useState('')
  const [outputName, setOutputName] = useState('')
  const [copied, setCopied] = useState(false)

  // Step 3 state — asset details + registry form
  const [uploadedAssetId, setUploadedAssetId] = useState(null)
  const [manualPlaybackId, setManualPlaybackId] = useState('')
  const [asset, setAsset] = useState(null)          // Mux asset JSON
  const [assetLoading, setAssetLoading] = useState(false)
  const [assetErr, setAssetErr] = useState(null)
  const [status, setStatus] = useState('')          // shared status line
  const [creating, setCreating] = useState(false)

  // Registry form state (prefilled from Mux asset)
  const [key, setKey] = useState('')
  const [label, setLabel] = useState('')
  const [kind, setKind] = useState('static')
  const [enabledFlag, setEnabledFlag] = useState(true)
  const [sortOrder, setSortOrder] = useState(0)

  // Auto-derive output name from source name (until editor customizes)
  const derivedOutputName = useMemo(() => {
    if (!sourceName.trim()) return ''
    const stem = sourceName.replace(/\.[^.]+$/, '')
    return `${stem}_transition_ready.mp4`
  }, [sourceName])
  const effectiveOutputName = outputName.trim() || derivedOutputName

  // Live-updating ffmpeg recipe. Matches the manual recipe used earlier
  // this session (produced spec-perfect output; the previous tool version
  // used -shortest which produced subtle AAC framing differences that
  // caused audio glitches at reel DIS boundaries). Now always:
  //   - reads source duration into $DUR via ffprobe
  //   - bounds the anullsrc silent audio to exactly $DUR
  //   - bounds the output to exactly $DUR
  //   - always adds silent AAC (bumpers should never carry their own audio)
  //   - explicit -map to force video-from-source + audio-from-anullsrc
  //     (defends against source files that have their own audio track —
  //     the source's audio would otherwise be preferred over the silent stream)
  const ffmpegCommand = useMemo(() => {
    const src = sourceName.trim() || '<source_file>'
    const out = effectiveOutputName || '<output_file>'
    const lines = [
      `SRC="${src}"`,
      `OUT="${out}"`,
      `DUR=$(ffprobe -v error -select_streams v:0 -show_entries stream=duration -of default=nw=1:nk=1 "$SRC")`,
      ``,
      `ffmpeg -y -i "$SRC" \\`,
      `  -f lavfi -t "$DUR" -i anullsrc=channel_layout=stereo:sample_rate=48000 \\`,
      `  -map 0:v:0 -map 1:a:0 \\`,
      `  -c:v libx264 -profile:v high -level 4.0 -pix_fmt yuv420p \\`,
      `    -crf 18 -preset slow \\`,
      `    -r 60000/1001 \\`,
      `    -colorspace bt709 -color_primaries bt709 -color_trc bt709 \\`,
      `  -c:a aac -b:a 128k -ar 48000 -ac 2 \\`,
      `  -t "$DUR" -movflags +faststart \\`,
      `  "$OUT"`,
    ]
    return lines.join('\n')
  }, [sourceName, effectiveOutputName])

  async function copyCommand() {
    try {
      await navigator.clipboard.writeText(ffmpegCommand)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  // Fetch Mux asset details when Step 2's upload completes OR when editor
  // pastes a playback ID manually. The playback-id path first resolves it
  // to an asset ID via the API.
  const loadAssetDetails = useCallback(async (assetIdOrPbId, isPlaybackId = false) => {
    setAssetLoading(true)
    setAssetErr(null)
    setAsset(null)
    try {
      let assetData
      if (isPlaybackId) {
        // Resolve playback ID → asset via /mux/playback-ids/:id then GET asset
        // The vader proxy exposes both /mux/assets and /mux/playback-ids/*
        const pbData = await rawApi(`/mux/playback-ids/${assetIdOrPbId}`)
        const resolvedAssetId = pbData?.data?.object?.id
        if (!resolvedAssetId) throw new Error('Playback ID did not resolve to an asset')
        const r = await rawApi(`/mux/assets/${resolvedAssetId}`)
        assetData = r?.data || r
      } else {
        const r = await rawApi(`/mux/assets/${assetIdOrPbId}`)
        assetData = r?.data || r
      }
      setAsset(assetData)
      // Prefill form fields — use the shared slugify helper so the key
      // is guaranteed to satisfy the backend KEY_PATTERN.
      const title = assetData?.meta?.title || ''
      const slug = slugifyKey(title).slice(0, 40) || 'new_bumper'
      setKey(prev => prev || slug)
      setLabel(prev => prev || (title || 'New bumper'))
      setStatus('')
    } catch (e) {
      setAssetErr(e.message || String(e))
    } finally {
      setAssetLoading(false)
    }
  }, [rawApi])

  function onUploadComplete(assetId) {
    setUploadedAssetId(assetId)
    setStatus(`Upload complete. Fetching Mux asset details…`)
    loadAssetDetails(assetId, false)
  }

  function onManualPasteSubmit() {
    const pbId = manualPlaybackId.trim()
    if (!pbId) return
    setStatus(`Resolving playback ID…`)
    loadAssetDetails(pbId, true)
  }

  // Spec compliance check — flag any deviation from TARGET_SPEC.
  // Uses the shared checkAssetSpec helper so BumperLibrary + BumperCreator
  // apply the same rules.
  const specCheck = useMemo(() => {
    if (!asset) return null
    const r = checkAssetSpec(asset)
    return { passed: r.ok, issues: r.issues }
  }, [asset])

  async function createRegistryEntry() {
    if (!asset) return
    const pbId = asset.playback_ids?.[0]?.id
    if (!pbId) {
      setStatus('Mux asset has no playback ID — cannot register.')
      return
    }
    setCreating(true)
    setStatus('Creating registry entry…')
    try {
      await api.createReelTransition({
        key: key.trim(),
        label: label.trim(),
        mux_playback_id: pbId,
        duration_seconds: asset.duration ? Number(asset.duration.toFixed(3)) : 1,
        kind,
        is_enabled: enabledFlag ? 1 : 0,
        sort_order: Number.isFinite(parseInt(sortOrder, 10)) ? parseInt(sortOrder, 10) : 0,
      })
      setStatus(`✅ Registered: ${key.trim()}`)
      // Refresh the library above + reset the form for next bumper
      onCreated()
      resetForm()
    } catch (e) {
      setStatus(`Error: ${e.message || String(e)}`)
    } finally {
      setCreating(false)
    }
  }

  function resetForm() {
    setSourceName('')
    setOutputName('')
    setSourceHasAudio(false)
    setForceDuration(false)
    setDurationSecs('')
    setUploadedAssetId(null)
    setManualPlaybackId('')
    setAsset(null)
    setAssetErr(null)
    setKey('')
    setLabel('')
    setKind('static')
    setEnabledFlag(true)
    setSortOrder(0)
  }

  // Shared input styles
  const inputStyle = {
    padding: '4px 8px', background: '#1a1a1a', border: '1px solid #333',
    borderRadius: 3, color: '#ddd', fontSize: 12,
  }
  const labelStyle = { fontSize: 11, color: '#888', marginBottom: 3, display: 'block' }

  return (
    <div style={{ border: '1px solid #333', borderRadius: 6, background: '#161616' }}>
      <div style={{
        padding: '10px 14px', borderBottom: '1px solid #2a2a2a',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <Upload size={16} color="#22c55e" />
        <div style={{ fontWeight: 600, fontSize: 14 }}>Create New Bumper</div>
        <span style={{ fontSize: 11, color: '#888' }}>
          3 steps · ffmpeg locally → upload to Mux → register
        </span>
      </div>

      <div style={{ padding: 14, display: 'grid', gridTemplateColumns: '1fr 260px', gap: 20 }}>
        {/* LEFT: 3-step flow */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* STEP 1: Command generator */}
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#ddd', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <StepBadge n={1} /> Generate the ffmpeg command
            </div>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>
              Fill in the source file name; the command below updates live. Copy it and run in Terminal
              to produce a spec-compliant MP4 that matches <code>black_1s</code> byte-for-byte.
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <label style={labelStyle}>Source filename</label>
                <input value={sourceName} onChange={e => setSourceName(e.target.value)}
                  placeholder="my_new_bumper.mov"
                  style={{ ...inputStyle, width: '100%', fontFamily: 'monospace' }} />
              </div>
              <div>
                <label style={labelStyle}>
                  Output filename {sourceName && !outputName.trim() && <span style={{ color: '#666' }}>(auto: {derivedOutputName})</span>}
                </label>
                <input value={outputName} onChange={e => setOutputName(e.target.value)}
                  placeholder={derivedOutputName || 'output.mp4'}
                  style={{ ...inputStyle, width: '100%', fontFamily: 'monospace' }} />
              </div>
            </div>

            <div style={{ fontSize: 11, color: '#666', marginBottom: 10, lineHeight: 1.5 }}>
              Recipe always adds a silent AAC audio track and forces output duration
              to match source (auto-detected via ffprobe). Any existing audio in the
              source is discarded — transitions never carry their own sound.
            </div>

            <div style={{ position: 'relative' }}>
              <pre style={{
                background: '#0a0a0a', border: '1px solid #333', borderRadius: 4,
                padding: '10px 12px', fontSize: 11, fontFamily: 'monospace',
                color: '#bbf7d0', overflow: 'auto', margin: 0,
                whiteSpace: 'pre', maxHeight: 240,
              }}>{ffmpegCommand}</pre>
              <button onClick={copyCommand}
                title="Copy command"
                style={{
                  position: 'absolute', top: 6, right: 6,
                  padding: '4px 8px', background: copied ? '#166534' : '#333',
                  border: 'none', borderRadius: 3,
                  color: copied ? '#bbf7d0' : '#ddd', cursor: 'pointer',
                  fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4,
                }}>
                <Copy size={11} /> {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>

          {/* STEP 2: Upload to Mux */}
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#ddd', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <StepBadge n={2} /> Upload to Mux
            </div>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>
              Drop the encoded MP4 below (browser uploads directly to Mux — file doesn't route through vader).
              Or if you've already uploaded via Mux Stream Manager, paste the playback ID below.
            </div>

            <MuxUpload onComplete={onUploadComplete} setStatus={setStatus} />

            <div style={{ marginTop: 10, padding: '10px 12px', background: '#0e0e0e', border: '1px dashed #333', borderRadius: 4 }}>
              <label style={labelStyle}>
                Already uploaded? Paste playback ID here to skip Step 2
              </label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input value={manualPlaybackId} onChange={e => setManualPlaybackId(e.target.value)}
                  placeholder="e.g. WPaFGAMVXkfV2v3u02lSiv4nabXx4FraIXjdgk0200wVg00"
                  style={{ ...inputStyle, flex: 1, fontFamily: 'monospace' }} />
                <button onClick={onManualPasteSubmit} disabled={!manualPlaybackId.trim() || assetLoading}
                  style={{
                    padding: '4px 12px', background: '#3b82f6', border: 'none', borderRadius: 3,
                    color: 'white', cursor: 'pointer', fontSize: 11, whiteSpace: 'nowrap',
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}>
                  {assetLoading ? <Loader2 size={11} className="spin" /> : <Search size={11} />} Load asset
                </button>
              </div>
            </div>
          </div>

          {/* STEP 3: Registry entry form */}
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#ddd', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <StepBadge n={3} /> Register in the bumper library
            </div>

            {!asset && !assetLoading && (
              <div style={{ padding: 12, background: '#0e0e0e', border: '1px dashed #333', borderRadius: 4, fontSize: 12, color: '#666' }}>
                Waiting for Mux upload (Step 2) or a pasted playback ID above…
              </div>
            )}

            {assetLoading && (
              <div style={{ padding: 12, background: '#0e0e0e', border: '1px solid #333', borderRadius: 4, fontSize: 12, color: '#888' }}>
                <Loader2 size={12} className="spin" style={{ verticalAlign: 'middle', marginRight: 6 }} />
                Fetching Mux asset details…
              </div>
            )}

            {assetErr && (
              <div style={{ padding: 10, background: 'rgba(220,38,38,0.08)', border: '1px solid #4a1414', borderRadius: 4, fontSize: 12, color: '#fca5a5' }}>
                Error: {assetErr}
              </div>
            )}

            {asset && (
              <div style={{ padding: 12, background: '#0e0e0e', border: '1px solid #333', borderRadius: 4 }}>
                {/* Spec check banner */}
                {specCheck && (
                  <div style={{
                    marginBottom: 12, padding: '8px 10px', borderRadius: 3, fontSize: 11,
                    background: specCheck.passed ? 'rgba(22,101,52,0.15)' : 'rgba(220,38,38,0.08)',
                    border: `1px solid ${specCheck.passed ? '#166534' : '#4a1414'}`,
                    color: specCheck.passed ? '#bbf7d0' : '#fca5a5',
                  }}>
                    {specCheck.passed ? (
                      <span><Check size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Asset meets transition spec — ready to register</span>
                    ) : (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, fontWeight: 600 }}>
                          <AlertTriangle size={12} /> Spec issues detected
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 16 }}>
                          {specCheck.issues.map((iss, i) => <li key={i}>{iss}</li>)}
                        </ul>
                      </>
                    )}
                  </div>
                )}

                {/* Asset details readout */}
                <div style={{ fontSize: 11, color: '#888', marginBottom: 12, fontFamily: 'monospace' }}>
                  <div>Playback ID: <span style={{ color: '#aaa' }}>{asset.playback_ids?.[0]?.id || '—'}</span></div>
                  <div>Duration: <span style={{ color: '#aaa' }}>{asset.duration ? `${asset.duration.toFixed(3)}s` : '—'}</span></div>
                  <div>Resolution: <span style={{ color: '#aaa' }}>{asset.max_stored_resolution || '—'}</span></div>
                  <div>Aspect: <span style={{ color: '#aaa' }}>{asset.aspect_ratio || '—'}</span></div>
                  <div>Frame rate: <span style={{ color: '#aaa' }}>{asset.max_stored_frame_rate || '—'} fps</span></div>
                </div>

                {/* Registry form */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={labelStyle}>Registry key <span style={{ color: '#666' }}>(a-z, 0-9, _ — auto-slugified)</span></label>
                    <input value={key} onChange={e => setKey(slugifyKey(e.target.value))}
                      placeholder="e.g. brand_bumper_3s"
                      style={{ ...inputStyle, width: '100%', fontFamily: 'monospace' }} />
                    {key && !KEY_PATTERN.test(key) && (
                      <div style={{ fontSize: 10, color: '#f59e0b', marginTop: 2 }}>
                        Only lowercase letters, digits, and underscores allowed
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={labelStyle}>Label (editor-visible name)</label>
                    <input value={label} onChange={e => setLabel(e.target.value)}
                      style={{ ...inputStyle, width: '100%' }} />
                  </div>
                  <div>
                    <label style={labelStyle}>Kind</label>
                    <select value={kind} onChange={e => setKind(e.target.value)}
                      style={{ ...inputStyle, width: '100%' }}>
                      <option value="static">static</option>
                      <option value="video">video</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Sort order</label>
                    <input type="number" step="1" value={sortOrder}
                      onChange={e => setSortOrder(e.target.value)}
                      style={{ ...inputStyle, width: '100%' }} />
                  </div>
                  <div style={{ gridColumn: '1 / span 2' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#ddd', cursor: 'pointer' }}>
                      <input type="checkbox" checked={enabledFlag} onChange={e => setEnabledFlag(e.target.checked)} />
                      Enabled (visible in ReelEditor per-gap picker)
                    </label>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button onClick={createRegistryEntry}
                    disabled={creating || !key.trim() || !label.trim() || !KEY_PATTERN.test(key.trim())}
                    style={{
                      padding: '6px 14px', background: '#22c55e', border: 'none', borderRadius: 4,
                      color: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 500,
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}>
                    {creating ? <Loader2 size={12} className="spin" /> : <Zap size={12} />}
                    Create registry entry
                  </button>
                  <button onClick={resetForm} disabled={creating}
                    style={{
                      padding: '6px 14px', background: '#333', border: 'none', borderRadius: 4,
                      color: '#ddd', cursor: 'pointer', fontSize: 12,
                    }}>
                    Reset
                  </button>
                </div>
              </div>
            )}

            {status && (
              <div style={{
                marginTop: 10, padding: '6px 10px', fontSize: 12, borderRadius: 3,
                background: status.startsWith('Error') ? 'rgba(220,38,38,0.08)' : 'rgba(59,130,246,0.08)',
                color: status.startsWith('Error') ? '#fca5a5' : '#93c5fd',
              }}>
                {status}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: sticky spec card */}
        <SpecCard />
      </div>
    </div>
  )
}

function StepBadge({ n }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 22, height: 22, borderRadius: 11,
      background: '#3b82f6', color: 'white', fontSize: 11, fontWeight: 600,
    }}>{n}</span>
  )
}

// Spec compliance badge for a Library row. Green ✓ when the Mux asset
// matches TARGET_SPEC, amber ⚠️ with issue list in the tooltip when it
// doesn't. Silent when the check hasn't run yet.
function SpecBadge({ result }) {
  if (!result) return null
  const isOk = result.ok
  return (
    <span
      title={isOk
        ? 'Meets transition spec'
        : `Spec issues:\n\n${result.issues.join('\n')}`}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        marginLeft: 6, width: 16, height: 16, borderRadius: 8,
        background: isOk ? '#166534' : '#78350f',
        color: isOk ? '#bbf7d0' : '#fed7aa',
        fontSize: 10, fontWeight: 700, cursor: 'help',
        verticalAlign: 'middle',
      }}>
      {isOk ? '✓' : '!'}
    </span>
  )
}

function SpecCard() {
  return (
    <div style={{
      alignSelf: 'flex-start', position: 'sticky', top: 0,
      padding: 12, background: '#0a0a0a', border: '1px solid #333', borderRadius: 4,
      fontSize: 11,
    }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#ddd', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Film size={12} color="#3b82f6" /> Target spec
      </div>
      <div style={{ color: '#888', marginBottom: 10, lineHeight: 1.5 }}>
        Matches <code style={{ color: '#93c5fd' }}>black_1s</code> byte-for-byte.
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', color: '#aaa' }}>
        <tbody>
          <SpecRow k="Container" v={TARGET_SPEC.container} />
          <SpecRow k="Video codec" v={TARGET_SPEC.video_codec} />
          <SpecRow k="Resolution" v={`${TARGET_SPEC.width}×${TARGET_SPEC.height}`} />
          <SpecRow k="Frame rate" v={`${TARGET_SPEC.fps_display} (60000/1001)`} />
          <SpecRow k="Pixel format" v={TARGET_SPEC.pix_fmt} />
          <SpecRow k="Color" v={TARGET_SPEC.color} />
          <SpecRow k="Audio codec" v={TARGET_SPEC.audio_codec} />
          <SpecRow k="Sample rate" v={`${TARGET_SPEC.audio_sample_rate} Hz`} />
          <SpecRow k="Channels" v={`${TARGET_SPEC.audio_channels} (stereo)`} />
          <SpecRow k="Bitrate" v={`~${TARGET_SPEC.audio_bitrate_kbps} kbps`} />
        </tbody>
      </table>
      <div style={{ marginTop: 10, fontSize: 10, color: '#666', lineHeight: 1.5 }}>
        Missing audio, wrong sample rate, or mismatched frame rate can cause
        decoder confusion at reel <code>DISCONTINUITY</code> boundaries — that's
        the "xylophone raiders" glitch we tracked down. The ffmpeg recipe
        produces all of the above.
      </div>
    </div>
  )
}

function SpecRow({ k, v }) {
  return (
    <tr>
      <td style={{ padding: '2px 6px 2px 0', color: '#666', whiteSpace: 'nowrap' }}>{k}</td>
      <td style={{ padding: '2px 0', fontFamily: 'monospace' }}>{v}</td>
    </tr>
  )
}
